import { LogOrigin } from './LogOrigin';
import { convertLogStyleToString, getLogStyle, ILogStyle, LogStyles } from './LogStyles';
import { LogType } from './LogType';
import { isLocalHost } from '../Resource/ResourceManager';

// fixme: this dependency is pretty big because of all the imports Cheats.ts has; remove it asap
import { cheatsUsed } from '../../Cheats';

export enum LogThresholds {
    LOW = -0, // e.g. PROD
    MEDIUM = 500, // e.g. QA DEBUG
    HIGH = 1000, // i.e. anything  e.g. DEBUG
}

interface ILoggerSettings {
    style?:  LogStyles | ILogStyle;
    origin: LogOrigin;
    threshold?: LogThresholds;
    data?: Array<Object | string | number | boolean>;
}

/**
 * The default threshold level to compare against if the log call doesn't specify
 */
const DEFAULT_THRESHOLD_LEVEL: LogThresholds = LogThresholds.LOW;

/**
 * The current threshold level to compare against provided log events
 */
const ACTIVE_THRESHOLD_LEVEL: LogThresholds = LogThresholds.HIGH;

export class Logger {

    /**
     * @
     * @param _msg The message, data, or error to log
     * @param _logOrigin Where the log originates from (e.g. LogOrigin.PLAYER_STATE_HELPER)
     * @param _threshold Severity of the error
     * @param _style The style to apply to the message, either as an enum to predefined (preferred) or as an object
     *
     * @example
     * Logger.log(
     *   "charly off-screen again",
     *   LogOrigin.PLAYER_STATE_HELPER,
     *   LogStyles.VISIBLE,
     * );
     * @example
     * Logger.log(
     *   "charly off-screen again",
     *   {
     *       origin: LogOrigin.PLAYER_STATE_HELPER,
     *       style: LogStyles.VISIBLE,
     *       type: LogType.WARNING,
     *   }
     * );
     * @example
     * Logger.log(
     *   new Error("Ach! An error!"),
     *   LogOrigin.PLAYER_STATE_HELPER,
     * );
     */
    public static log(
        _msg: string | Error,
        _logOrigin?: LogOrigin | ILoggerSettings,
        _style?: LogStyles | ILogStyle,
        _threshold?: LogThresholds | number,
    ): void {
        /**
         * Initialize settings object to empty if no settings provided, otherwise load _logOrigin as settings
         */
        const settings: ILoggerSettings = (typeof _logOrigin === "object" ? _logOrigin : {}) as ILoggerSettings;
        if(!settings.data) settings.data = [];

        /**
         * For determining if the data is worth logging per threshold settings
         */
        const threshold: LogThresholds = Logger.getThreshold(settings.threshold || _threshold || DEFAULT_THRESHOLD_LEVEL);
        if(threshold > ACTIVE_THRESHOLD_LEVEL) {
            return;
        }

        /**
         * Determines if the _msg parameter is an Error (for purpose of auto stack tracing)
         */
        const isError: boolean = _msg instanceof Error;

        /**
         * Either _msg parameter as-is or the `.message` parameter from an Error
         */
        const message: string = (isError ? (_msg as Error).message : _msg as string);

        /**
         * For logging the origin as "[Origin] Message"; will use LogOrigin.UNKNOWN if none provided
         */
        const origin: LogOrigin = settings.origin || (_logOrigin as LogOrigin) || LogOrigin.UNKNOWN;

        /**
         * The game/CSS hybrid object for how to style the log
         */
        const style: ILogStyle = Logger.parseStyle(_style || settings.style);

        /**
         * `console.log` requires style provided as a CSS string so we need to convert it
         */
        const styleAsString: string = Boolean(style) ? convertLogStyleToString(style) : "";

        const logFunction: Function = (!style) ? (console.log) : (
            style.logAs === LogType.ERROR ?
                console.error
                :
                (
                    style.logAs === LogType.WARNING ?
                        console.warn
                        :
                        console.log
                )
        );

        const logWithStackTrace: boolean = (isError || style?.logWithStackTrace);
        const logWithLocation: boolean = ((isLocalHost || cheatsUsed ) && style?.logWithLocation);
        const stack: string | undefined = isError ? (_msg as Error).stack : (
            (
                style?.logWithStackTrace || style?.logWithLocation
            ) ? new Error().stack : undefined
        )

        if(logWithLocation) {
            let str: string = "" + stack;
            try {
                str = str.substr(str.indexOf("\n") + 1);
                str = str.substr(str.indexOf("\n") + 1);
                str = str.substr(0, str.indexOf("\n"));
                str = `(${str.substr(0, str.indexOf(")") + 1).trim().replace("(", "\n\t(")})`;
            } catch(err) {
                // where do you log when the Logger has an error? :thinking:
                Logger.log(err, LogOrigin.LOGGER, LogStyles.WARNING);
            }
            settings.data.push(str);
        }

        if(logWithStackTrace) {
            settings.data.push(stack.substr(stack.indexOf("    at Function")));
        }

        const logPrefix: string = `%c[${origin}]`;
        let logString: string = `${logPrefix} ${message}`.trim();
        if(Boolean(settings.data)) {
            // logString += " ";
            const count: number = (logString.match(/%i|%s|%f|%O/g) || []).length;
            for(let i: number = count; i < settings.data.length; i++) {
                switch(typeof settings.data[i]) {
                    case "string":
                        logString += "%s";
                        break;
                    case "number":
                        if(Math.floor(settings.data[i] as number) === settings.data[i]) {
                            logString += "%f";
                        } else {
                            logString += "%i";
                        }
                        break;
                    case "object":
                        logString += "%O";
                        break;
                }
                logString += " ";
            }
            settings.data.unshift(styleAsString);
            try {
                logFunction(logString, ...settings.data);
            } catch(err) {
                console.error(err);
            }
        } else {
            try {
                logFunction(logString, styleAsString);
            } catch(err) {
                console.error(err);
            }
        }
    }

    private static getThreshold(val: number): LogThresholds {
        if(val < LogThresholds.MEDIUM) {
            return LogThresholds.LOW;
        } else if(val < LogThresholds.HIGH) {
            return LogThresholds.MEDIUM;
        } else {
            return LogThresholds.HIGH;
        }
    }

    private static parseStyle(_style: unknown): ILogStyle | undefined {
        let retVal: ILogStyle = undefined;
        if (typeof _style !== "undefined") {
            switch(typeof _style) {
                case ("string"):
                case (typeof LogStyles.DEFAULT):
                    retVal = getLogStyle(_style as LogStyles);
                    break;
                case "object":
                    retVal = _style as ILogStyle;
                    break;
                default:
                    // do nothing; leave as undefined
                    break;
            }
        }
        return retVal;
    }

    public static runTest(): void {
        Logger.log(
            `Test ${1}`,
            LogOrigin.LOGGER_TEST
        );
        Logger.log(
            "Test %i",
            {
                origin: LogOrigin.LOGGER_TEST,
                data: [
                    2
                ]
            }
        );
        Logger.log(
            "%s %i",
            {
                origin: LogOrigin.LOGGER_TEST,
                data: [
                    "Test",
                    3
                ]
            }
        );
        Logger.log(
            "%s %i, in red",
            {
                origin: LogOrigin.LOGGER_TEST,
                style: {
                    "color": "red"
                },
                data: [
                    "Test",
                    4
                ]
            }
        );
        Logger.log(
            "Test 5, as a warning",
            {
                origin: LogOrigin.LOGGER_TEST,
                style: {
                    "color": "red",
                    logAs: LogType.WARNING
                }
            }
        );
        Logger.log(
            "Test 6, as an error",
            {
                origin: LogOrigin.LOGGER_TEST,
                style: {
                    "color": "red",
                    logAs: LogType.ERROR
                }
            }
        );
        Logger.log(
            new Error("Test 7, as an Error object"),
            {
                origin: LogOrigin.LOGGER_TEST,
                style: {
                    "color": "red"
                }
            }
        );
        Logger.log(
            "Test 8, as a location",
            {
                origin: LogOrigin.LOGGER_TEST,
                style: {
                    logWithLocation: true
                }
            }
        );
    }
}

// Uncomment for perf test
/**
    const start: number = performance.now();
    for(let i = 0; i < 100; i++) {
        Logger.log("test", LogOrigin.UNKNOWN, LogThresholds.HIGH, LogStyles.VISIBLE);
    }
    const end: number = performance.now();
    alert("time taken: " + (end - start));
*/

// Uncomment for tests on creation
/***/
    // Logger.runTest();

